// services/SimpleBillPrintService.js
/**
 * Simple Bill Print Service - Clean bill format without UPI/Bank details
 * Direct printing without new tabs
 */

class SimpleBillPrintService {
  constructor() {
    this.paperSizes = {
      a4: {
        name: 'A4',
        css: `
          @page { 
            size: A4; 
            margin: 0.5in; 
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 11pt;
            line-height: 1.3;
            color: #000;
          }
        `
      },
      a5: {
        name: 'A5',
        css: `
          @page { 
            size: A5; 
            margin: 0.3in; 
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 10pt;
            line-height: 1.2;
          }
        `
      },
      thermal: {
        name: 'Thermal',
        css: `
          @page { 
            size: 80mm 297mm; 
            margin: 2mm; 
          }
          body { 
            font-family: 'Courier New', monospace;
            font-size: 9pt;
            line-height: 1.2;
          }
          .bill {
            width: 72mm;
            margin: 0 auto;
          }
        `,
        isThermal: true
      },
      'thermal-58': {
        name: 'Thermal 58mm',
        css: `
          @page { 
            size: 58mm 297mm; 
            margin: 2mm; 
          }
          body { 
            font-family: 'Courier New', monospace;
            font-size: 8pt;
            line-height: 1.2;
          }
          .bill {
            width: 52mm;
            margin: 0 auto;
          }
        `,
        isThermal: true
      },
      letter: {
        name: 'Letter',
        css: `
          @page { 
            size: Letter; 
            margin: 0.5in; 
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 11pt;
          }
        `
      }
    };
  }

  /**
   * Direct print without opening new tab
   */
  print(data) {
    try {
      const safeData = this.sanitizeData(data);
      const html = this.generateBillHTML(safeData);
      this.directPrint(html);
      return true;
    } catch (error) {
      console.error('Print error:', error);
      throw new Error(`Failed to print: ${error.message}`);
    }
  }

  /**
   * Preview in new tab
   */
  preview(data) {
    try {
      const safeData = this.sanitizeData(data);
      const html = this.generateBillHTML(safeData);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
      return win;
    } catch (error) {
      console.error('Preview error:', error);
      alert(`Failed to preview: ${error.message}`);
    }
  }

  /**
   * Direct print using hidden iframe
   */
  directPrint(html) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();
    
    iframe.contentWindow.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  /**
   * Sanitize input data
   */
  sanitizeData(data) {
    return {
      settings: data.settings || {},
      company: data.company || { name: '', address: '', phone: '' },
      customer: data.customer || { name: 'Guest', mobile: '', address: '' },
      items: Array.isArray(data.items) ? data.items : [],
      totals: data.totals || {
        subtotal: 0,
        totalDiscount: 0,
        totalCharges: 0,
        roundedTotal: 0,
        roundOff: 0
      },
      billNo: data.billNo || 'N/A',
      billDate: data.billDate || new Date().toISOString().split('T')[0],
      paymentMode: data.paymentMode || 'Cash',
      notes: data.notes || '',
      terms: data.terms || 'Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation.'
    };
  }

  /**
   * Generate bill HTML
   */
  generateBillHTML(data) {
    const { company, customer, items, totals, billNo, billDate, paymentMode, notes, terms, settings } = data;
    
    let paperSize = this.paperSizes.a4;
    if (settings.billSize === 'a5') {
      paperSize = this.paperSizes.a5;
    } else if (settings.billSize === 'thermal-80' || settings.billSize === 'thermal') {
      paperSize = this.paperSizes.thermal;
    } else if (settings.billSize === 'thermal-58') {
      paperSize = this.paperSizes['thermal-58'];
    } else if (settings.billSize === 'letter') {
      paperSize = this.paperSizes.letter;
    }
    
    const isThermal = paperSize.isThermal;
    
    if (isThermal) {
      return this.generateThermalBill(data, paperSize);
    }
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill - ${billNo}</title>
          <meta charset="UTF-8">
          <style>
            ${paperSize.css}
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              background: white;
              padding: 20px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .bill-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
            }
            
            .company-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            
            .company-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              text-transform: uppercase;
            }
            
            .company-address {
              font-size: 11px;
              color: #333;
              margin-bottom: 3px;
            }
            
            .company-phone {
              font-size: 11px;
              color: #333;
            }
            
            .bill-title {
              text-align: center;
              margin: 15px 0;
            }
            
            .bill-title h2 {
              font-size: 20px;
              font-weight: bold;
              letter-spacing: 2px;
              padding: 5px 20px;
              display: inline-block;
              border-top: 1px solid #000;
              border-bottom: 1px solid #000;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 20px;
              border: 1px solid #000;
              padding: 12px;
            }
            
            .info-section {
              font-size: 11px;
            }
            
            .info-section .section-title {
              font-weight: bold;
              margin-bottom: 8px;
              font-size: 12px;
              text-decoration: underline;
            }
            
            .info-row {
              margin: 5px 0;
            }
            
            .info-label {
              font-weight: bold;
              min-width: 80px;
              display: inline-block;
            }
            
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            
            .items-table th {
              background: #f0f0f0;
              border: 1px solid #000;
              padding: 10px 8px;
              font-weight: bold;
              text-align: center;
              font-size: 12px;
            }
            
            .items-table td {
              border: 1px solid #000;
              padding: 8px;
              font-size: 11px;
              vertical-align: top;
            }
            
            .items-table td:first-child {
              text-align: center;
            }
            
            .items-table td:nth-child(3),
            .items-table td:nth-child(4) {
              text-align: right;
            }
            
            .totals-section {
              width: 100%;
              margin-top: 20px;
            }
            
            .totals-table {
              width: 300px;
              margin-left: auto;
              border-collapse: collapse;
            }
            
            .totals-table td {
              padding: 6px 8px;
              font-size: 12px;
            }
            
            .totals-table td:first-child {
              text-align: left;
            }
            
            .totals-table td:last-child {
              text-align: right;
            }
            
            .grand-total {
              font-weight: bold;
              font-size: 14px;
              border-top: 2px solid #000;
            }
            
            .amount-words {
              margin-top: 15px;
              padding: 8px;
              border: 1px solid #000;
              font-size: 11px;
              font-style: italic;
              background: #fafafa;
            }
            
            .footer-notes {
              margin-top: 20px;
              padding: 12px;
              border: 1px solid #000;
              font-size: 10px;
            }
            
            .footer-notes p {
              margin: 3px 0;
            }
            
            .thankyou {
              text-align: center;
              font-weight: bold;
              margin-top: 15px;
              font-size: 12px;
            }
            
            .signature-section {
              display: flex;
              justify-content: space-between;
              margin-top: 40px;
              padding-top: 20px;
            }
            
            .signature-box {
              text-align: center;
              width: 200px;
            }
            
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 40px;
              padding-top: 5px;
              font-size: 10px;
            }
            
            .eoe {
              text-align: center;
              font-size: 9px;
              margin-top: 15px;
              color: #666;
            }
            
            @media print {
              body {
                padding: 0;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="bill-container">
            <div class="company-header">
              <div class="company-name">${this.escapeHtml(company.name || 'YOUR COMPANY NAME')}</div>
              <div class="company-address">${this.escapeHtml(company.address || '')}</div>
              <div class="company-phone">📞 ${this.escapeHtml(company.phone || '')}</div>
            </div>
            
            <div class="bill-title">
              <h2>BILL</h2>
            </div>
            
            <div class="info-grid">
              <div class="info-section">
                <div class="section-title">Details of Supplier</div>
                <div class="info-row">
                  <span class="info-label">Name:</span> ${this.escapeHtml(company.name || '')}
                </div>
                <div class="info-row">
                  <span class="info-label">Address:</span> ${this.escapeHtml(company.address || '')}
                </div>
                <div class="info-row">
                  <span class="info-label">Phone:</span> ${this.escapeHtml(company.phone || '')}
                </div>
              </div>
              <div class="info-section">
                <div class="section-title">Details of Receiver</div>
                <div class="info-row">
                  <span class="info-label">Name:</span> ${this.escapeHtml(customer.name || 'Guest')}
                </div>
                ${customer.mobile ? `
                <div class="info-row">
                  <span class="info-label">Mobile No.:</span> ${this.escapeHtml(customer.mobile)}
                </div>
                ` : ''}
                ${customer.address ? `
                <div class="info-row">
                  <span class="info-label">Address:</span> ${this.escapeHtml(customer.address)}
                </div>
                ` : ''}
              </div>
            </div>
            
            <div class="info-grid" style="margin-top: -15px;">
              <div class="info-section">
                <div class="info-row">
                  <span class="info-label">Bill No.:</span> ${this.escapeHtml(billNo)}
                </div>
                <div class="info-row">
                  <span class="info-label">Date:</span> ${this.formatDate(billDate)}
                </div>
              </div>
              <div class="info-section">
                <div class="info-row">
                  <span class="info-label">Payment Mode:</span> ${this.escapeHtml(paymentMode)}
                </div>
              </div>
            </div>
            
            <table class="items-table">
              <thead>
                <tr>
                  <th width="5%">#</th>
                  <th width="50%">Description of Product/Services</th>
                  <th width="10%">Quantity</th>
                  <th width="15%">Rate (₹)</th>
                  <th width="20%">Amount (₹)</th>
                 </tr>
              </thead>
              <tbody>
                ${items.map((item, idx) => `
                  <tr>
                    <td>${idx + 1}</td>
                    <td>${this.escapeHtml(item.name)}</td>
                    <td style="text-align: right;">${item.qty}</td>
                    <td style="text-align: right;">${this.formatNumber(item.price)}</td>
                    <td style="text-align: right;">${this.formatNumber(item.netTotal)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="totals-section">
              <table class="totals-table">
                <tr>
                  <td>Subtotal:</td>
                  <td>₹ ${this.formatNumber(totals.subtotal)}</td>
                </tr>
                ${totals.totalDiscount > 0 ? `
                <tr>
                  <td>Discount:</td>
                  <td>- ₹ ${this.formatNumber(totals.totalDiscount)}</td>
                </tr>
                ` : ''}
                ${totals.totalCharges > 0 ? `
                <tr>
                  <td>Additional Charges:</td>
                  <td>+ ₹ ${this.formatNumber(totals.totalCharges)}</td>
                </tr>
                ` : ''}
                ${totals.roundOff !== 0 ? `
                <tr>
                  <td>Round Off:</td>
                  <td>${totals.roundOff > 0 ? '+' : ''} ₹ ${this.formatNumber(Math.abs(totals.roundOff))}</td>
                </tr>
                ` : ''}
                <tr class="grand-total">
                  <td><strong>TOTAL</strong></td>
                  <td><strong>₹ ${this.formatNumber(totals.roundedTotal)}</strong></td>
                </tr>
              </table>
            </div>
            
            <div class="amount-words">
              <strong>Amount in Words:</strong> ${this.numberToWords(totals.roundedTotal)}
            </div>
            
            <div class="footer-notes">
              <p><strong>Notes:</strong> ${this.escapeHtml(notes) || 'N/A'}</p>
              <p><strong>Terms & Conditions:</strong></p>
              <p>${this.escapeHtml(terms || 'Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation.')}</p>
            </div>
            
            <div class="thankyou">
              Thank You! Visit Again
            </div>
            
            <div class="signature-section">
              <div class="signature-box">
                <div class="signature-line">Receiver's Signature</div>
              </div>
              <div class="signature-box">
                <div class="signature-line">Authorised Signatory</div>
              </div>
            </div>
            
            <div class="eoe">
              E.&O.E | This is a computer generated bill
            </div>
          </div>
        </body>
      </html>
    `;
  }

  /**
   * Generate thermal bill
   */
  generateThermalBill(data, paperSize) {
    const { company, customer, items, totals, billNo, billDate, paymentMode, notes, terms } = data;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill</title>
          <meta charset="UTF-8">
          <style>
            ${paperSize.css}
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              margin: 0;
              padding: 2mm;
              font-family: 'Courier New', monospace;
              font-size: 9pt;
            }
            
            .bill {
              width: 100%;
              margin: 0 auto;
            }
            
            .center {
              text-align: center;
            }
            
            .company-name {
              font-size: 12px;
              font-weight: bold;
              margin-bottom: 3px;
              text-transform: uppercase;
            }
            
            .company-details {
              font-size: 7px;
              margin-bottom: 3px;
            }
            
            .bill-title {
              font-size: 11px;
              font-weight: bold;
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
              padding: 3px 0;
              margin: 5px 0;
              text-align: center;
            }
            
            .info {
              font-size: 7px;
              margin: 5px 0;
              border-bottom: 1px dotted #000;
              padding-bottom: 5px;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
            }
            
            .items {
              width: 100%;
              margin: 5px 0;
            }
            
            .item-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              border-bottom: 1px dotted #000;
              margin-bottom: 3px;
              padding-bottom: 2px;
              font-size: 7px;
            }
            
            .item-row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
              font-size: 7px;
            }
            
            .item-name {
              flex: 2;
              word-break: break-word;
            }
            
            .item-qty {
              width: 35px;
              text-align: right;
            }
            
            .item-rate {
              width: 45px;
              text-align: right;
            }
            
            .item-amount {
              width: 55px;
              text-align: right;
            }
            
            .divider {
              border-top: 1px dashed #000;
              margin: 5px 0;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              margin: 3px 0;
            }
            
            .grand-total {
              font-weight: bold;
              font-size: 9px;
              border-top: 1px solid #000;
              padding-top: 3px;
              margin-top: 3px;
            }
            
            .amount-words {
              font-size: 6px;
              margin: 5px 0;
              padding: 3px;
              border-top: 1px dotted #000;
            }
            
            .footer {
              font-size: 6px;
              text-align: center;
              margin-top: 8px;
              border-top: 1px dashed #000;
              padding-top: 5px;
            }
            
            .signature {
              display: flex;
              justify-content: space-between;
              margin-top: 10px;
              font-size: 6px;
            }
            
            .signature-line {
              border-top: 1px dotted #000;
              width: 70px;
              margin-top: 15px;
              padding-top: 2px;
              text-align: center;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="bill">
            <div class="center">
              <div class="company-name">${this.escapeHtml(company.name || 'COMPANY NAME')}</div>
              <div class="company-details">${this.escapeHtml(company.address || '')}</div>
              <div class="company-details">📞 ${this.escapeHtml(company.phone || '')}</div>
            </div>
            
            <div class="bill-title">BILL</div>
            
            <div class="info">
              <div class="info-row">
                <span>Bill No: ${this.escapeHtml(billNo)}</span>
                <span>Date: ${this.formatDate(billDate, true)}</span>
              </div>
              <div class="info-row">
                <span>Customer: ${this.escapeHtml(customer.name || 'Guest')}</span>
                <span>${customer.mobile ? this.escapeHtml(customer.mobile) : ''}</span>
              </div>
              <div class="info-row">
                <span>Payment: ${this.escapeHtml(paymentMode)}</span>
              </div>
            </div>
            
            <div class="items">
              <div class="item-header">
                <span>Item</span>
                <span>Qty Rate Amount</span>
              </div>
              ${items.map(item => `
                <div class="item-row">
                  <div class="item-name">${this.escapeHtml(item.name)}</div>
                  <div class="item-qty">${item.qty}</div>
                  <div class="item-rate">${this.formatNumber(item.price)}</div>
                  <div class="item-amount">${this.formatNumber(item.netTotal)}</div>
                </div>
              `).join('')}
            </div>
            
            <div class="divider"></div>
            
            <div class="total-row">
              <span>Subtotal:</span>
              <span>${this.formatNumber(totals.subtotal)}</span>
            </div>
            ${totals.totalDiscount > 0 ? `
              <div class="total-row">
                <span>Discount:</span>
                <span>-${this.formatNumber(totals.totalDiscount)}</span>
              </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>TOTAL:</span>
              <span>${this.formatNumber(totals.roundedTotal)}</span>
            </div>
            
            <div class="amount-words">
              ${this.numberToWords(totals.roundedTotal)}
            </div>
            
            <div class="info" style="font-size: 6px;">
              ${notes ? `<div>Notes: ${this.escapeHtml(notes)}</div>` : ''}
              <div>${this.escapeHtml(terms || 'Goods once sold will not be taken back')}</div>
            </div>
            
            <div class="signature">
              <div>
                <div class="signature-line">Receiver's Signature</div>
              </div>
              <div>
                <div class="signature-line">Authorised Signatory</div>
              </div>
            </div>
            
            <div class="footer">
              Thank You! Visit Again<br>
              E.&O.E | ${new Date().toLocaleString()}
            </div>
          </div>
        </body>
      </html>
    `;
  }

  formatDate(date, short = false) {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (short) {
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  formatNumber(num) {
    if (num === undefined || num === null) return '0.00';
    return num.toFixed(2);
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  numberToWords(num) {
    if (num === 0) return 'Zero Only';
    
    const single = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const double = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      
      let str = '';
      
      if (n >= 100) {
        str += single[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        str += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        str += double[n - 10] + ' ';
        n = 0;
      }
      
      if (n > 0) {
        str += single[n] + ' ';
      }
      
      return str;
    };

    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let result = '';
    
    const crore = Math.floor(rupees / 10000000);
    const lakh = Math.floor((rupees % 10000000) / 100000);
    const thousand = Math.floor((rupees % 100000) / 1000);
    const hundred = rupees % 1000;
    
    if (crore > 0) {
      result += convertLessThanThousand(crore) + 'Crore ';
    }
    
    if (lakh > 0) {
      result += convertLessThanThousand(lakh) + 'Lakh ';
    }
    
    if (thousand > 0) {
      result += convertLessThanThousand(thousand) + 'Thousand ';
    }
    
    if (hundred > 0) {
      result += convertLessThanThousand(hundred);
    }
    
    result = result.trim() + ' Rupees';
    
    if (paise > 0) {
      result += ' and ' + convertLessThanThousand(paise).trim() + ' Paise';
    }
    
    return result + ' Only';
  }
}

export default SimpleBillPrintService;